// src/hooks/useAdminServices.js
import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

export const useAdminServices = () => {
  const { user, isAdmin } = useAuth();
  const [services, setServices] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [serviceListMeta, setServiceListMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingServices, setUpdatingServices] = useState(false);
  const [error, setError] = useState(null);

  // Load services (customer services) from Firestore
  const loadServices = useCallback(async () => {
    if (!user?.uid || !isAdmin) {
      setServices([]);
      setLoading(false);
      return;
    }

    try {
      const servicesRef = collection(db, 'services');
      const q = query(
        servicesRef,
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        const servicesList = [];

        for (const serviceDoc of querySnapshot.docs) {
          const serviceData = serviceDoc.data();

          try {
            // Fetch related customer data
            let customer = null;
            if (serviceData.customerId) {
              const customerDoc = await getDoc(doc(db, 'users', serviceData.customerId));
              if (customerDoc.exists()) {
                const customerData = customerDoc.data();
                customer = {
                  id: customerDoc.id,
                  name: `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim() || 
                        customerData.email || 'Unknown Customer',
                  email: customerData.email
                };
              }
            }

            // Fetch related property data
            let property = null;
            if (serviceData.propertyId) {
              const propertyDoc = await getDoc(doc(db, 'properties', serviceData.propertyId));
              if (propertyDoc.exists()) {
                const propertyData = propertyDoc.data();
                property = {
                  id: propertyDoc.id,
                  name: propertyData.name || 'Unnamed Property',
                  address: propertyData.address,
                  city: propertyData.city,
                  state: propertyData.state,
                  zipCode: propertyData.zipCode
                };
              }
            }

            servicesList.push({
              id: serviceDoc.id,
              ...serviceData,
              customer,
              property,
              
              // Add computed fields for display
              serviceType: serviceData.service?.name || 'Unknown Service',
              
              // Convert timestamps
              createdAt: serviceData.createdAt?.toDate() || new Date(),
              updatedAt: serviceData.updatedAt?.toDate() || new Date(),
              nextBillingDate: serviceData.nextBillingDate?.toDate() || null,
              billingCycleStart: serviceData.billingCycleStart?.toDate() || null
            });
          } catch (err) {
            console.error('Error processing service:', serviceDoc.id, err);
            // Add service without relationships if there's an error
            servicesList.push({
              id: serviceDoc.id,
              ...serviceData,
              customer: null,
              property: null,
              
              // Add computed fields for display
              serviceType: serviceData.service?.name || 'Unknown Service',
              
              // Convert timestamps
              createdAt: serviceData.createdAt?.toDate() || new Date(),
              updatedAt: serviceData.updatedAt?.toDate() || new Date(),
              nextBillingDate: serviceData.nextBillingDate?.toDate() || null,
              billingCycleStart: serviceData.billingCycleStart?.toDate() || null
            });
          }
        }
        
        setServices(servicesList);
      }, (err) => {
        console.error('Error listening to services:', err);
        setError('Failed to load services. Please try again.');
      });

      return unsubscribe;
    } catch (err) {
      setError('Failed to load services. Please try again.');
      console.error('Services loading error:', err);
    }
  }, [user?.uid, isAdmin]);

  // Load service list from Firestore
  const loadServiceList = useCallback(() => {
    if (!user?.uid || !isAdmin) {
      setServiceList([]);
      setServiceListMeta(null);
      return;
    }

    const serviceListRef = doc(db, 'service_list', 'main');

    // Set up real-time listener for service list
    const unsubscribe = onSnapshot(
      serviceListRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          
          // Handle both old array format and new object format
          let services = data.services || [];
          
          // Convert old string array to new object format
          if (services.length > 0 && typeof services[0] === 'string') {
            services = services.map((serviceName, index) => ({
              name: serviceName,
              description: '',
              icon: 'FaTools',
              displayOrder: index + 1 // Assign display order based on current position
            }));
          }
          
          // Ensure all services have a displayOrder
          services = services.map((service, index) => ({
            ...service,
            displayOrder: service.displayOrder !== undefined ? service.displayOrder : index + 1
          }));
          
          // Sort by displayOrder
          services.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
          
          setServiceList(services);
          
          // Store metadata for last updated info
          setServiceListMeta({
            updatedAt: data.updatedAt,
            lastUpdatedBy: data.lastUpdatedBy,
            createdAt: data.createdAt,
            createdBy: data.createdBy
          });
        } else {
          console.log('Service list document not found');
          setServiceList([]);
          setServiceListMeta(null);
        }
      },
      (err) => {
        console.error('Error listening to service list:', err);
        setError('Failed to load service list. Please try again.');
      });

    return unsubscribe;
  }, [user?.uid, isAdmin]);

  // Initialize with real data
  useEffect(() => {
    setLoading(true);
    
    const unsubscribeServices = loadServices();
    const unsubscribeServiceList = loadServiceList();

    // Set loading to false after both are set up
    setLoading(false);

    return () => {
      if (unsubscribeServices && typeof unsubscribeServices === 'function') {
        unsubscribeServices();
      }
      if (unsubscribeServiceList && typeof unsubscribeServiceList === 'function') {
        unsubscribeServiceList();
      }
    };
  }, [loadServices, loadServiceList]);

  // Create a new service
  const createService = async (serviceData) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to create services');
    }

    setUpdatingServices(true);
    setError(null);

    try {
      const newService = {
        ...serviceData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user.uid
      };

      await addDoc(collection(db, 'services'), newService);
    } catch (err) {
      setError('Failed to create service. Please try again.');
      console.error('Create service error:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  // Update an existing service
  const updateService = async (serviceId, updates) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to update services');
    }

    setUpdatingServices(true);
    setError(null);

    try {
      const serviceRef = doc(db, 'services', serviceId);
      await updateDoc(serviceRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.uid
      });
    } catch (err) {
      setError('Failed to update service. Please try again.');
      console.error('Update service error:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  // Delete a service
  const deleteService = async (serviceId) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to delete services');
    }

    setUpdatingServices(true);
    setError(null);

    try {
      const serviceRef = doc(db, 'services', serviceId);
      await deleteDoc(serviceRef);
    } catch (err) {
      setError('Failed to delete service. Please try again.');
      console.error('Delete service error:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  // Get service statistics
  const getServiceStats = () => {
    const totalServices = services.length;
    const activeServices = services.filter(s => s.status === 'active').length;
    const pausedServices = services.filter(s => s.status === 'paused').length;
    const completedServices = services.filter(s => s.status === 'completed').length;
    
    const monthlyRevenue = services
      .filter(s => s.status === 'active')
      .reduce((sum, service) => sum + (service.monthlyBillingAmount || 0), 0);

    const recurringServices = services.filter(s => 
      s.jobFrequency && s.jobFrequency !== 'one-time' && s.status === 'active'
    ).length;

    return {
      totalServices,
      activeServices,
      pausedServices,
      completedServices,
      monthlyRevenue,
      recurringServices,
      uniqueCustomers: new Set(services.map(s => s.customerId)).size,
      uniqueProperties: new Set(services.map(s => s.propertyId)).size
    };
  };

  // Get services by status
  const getServicesByStatus = (status) => {
    return services.filter(service => service.status === status);
  };

  // Get services by customer
  const getServicesByCustomer = (customerId) => {
    return services.filter(service => service.customerId === customerId);
  };

  // Get services with upcoming billing (next 7 days)
  const getUpcomingServices = (days = 7) => {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + days);

    return services.filter(service => {
      if (!service.nextBillingDate || service.status !== 'active') return false;
      return service.nextBillingDate >= now && service.nextBillingDate <= futureDate;
    }).sort((a, b) => a.nextBillingDate - b.nextBillingDate);
  };

  // Service list management functions
  const addServiceToList = async (serviceData) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to add services');
    }

    const trimmedName = serviceData.name?.trim();
    if (!trimmedName) {
      throw new Error('Service name cannot be empty');
    }

    if (serviceList.some(service => service.name.toLowerCase() === trimmedName.toLowerCase())) {
      throw new Error('Service already exists');
    }

    setUpdatingServices(true);
    setError(null);

    try {
      // Get the next display order (add to end)
      const maxOrder = serviceList.reduce((max, service) => 
        Math.max(max, service.displayOrder || 0), 0
      );

      const newService = {
        name: trimmedName,
        description: serviceData.description || '',
        icon: serviceData.icon || 'FaTools',
        displayOrder: maxOrder + 1
      };

      const updatedServices = [...serviceList, newService];
      const serviceListRef = doc(db, 'service_list', 'main');
      
      await updateDoc(serviceListRef, {
        services: updatedServices,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.uid
      });
    } catch (err) {
      setError('Failed to add service. Please try again.');
      console.error('Error adding service:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  const updateServiceInList = async (oldName, serviceData) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to update services');
    }

    const trimmedNewName = serviceData.name?.trim();
    if (!trimmedNewName) {
      throw new Error('Service name cannot be empty');
    }

    if (serviceList.some(service => 
      service.name.toLowerCase() === trimmedNewName.toLowerCase() && 
      service.name !== oldName
    )) {
      throw new Error('Service name already exists');
    }

    setUpdatingServices(true);
    setError(null);

    try {
      const updatedServices = serviceList.map(service => 
        service.name === oldName 
          ? { 
              ...service,
              name: trimmedNewName, 
              description: serviceData.description || '', 
              icon: serviceData.icon || 'FaTools'
            }
          : service
      );

      const serviceListRef = doc(db, 'service_list', 'main');
      
      await updateDoc(serviceListRef, {
        services: updatedServices,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.uid
      });
    } catch (err) {
      setError('Failed to update service. Please try again.');
      console.error('Error updating service:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  const deleteServiceFromList = async (serviceName) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to delete services');
    }

    setUpdatingServices(true);
    setError(null);

    try {
      const updatedServices = serviceList.filter(service => service.name !== serviceName);
      
      // Reorder remaining services to fill gaps
      const reorderedServices = updatedServices.map((service, index) => ({
        ...service,
        displayOrder: index + 1
      }));
      
      const serviceListRef = doc(db, 'service_list', 'main');
      
      await updateDoc(serviceListRef, {
        services: reorderedServices,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.uid
      });
    } catch (err) {
      setError('Failed to delete service. Please try again.');
      console.error('Error deleting service:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  // NEW: Move service up in the list
  const moveServiceUp = async (serviceName) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to reorder services');
    }

    const currentIndex = serviceList.findIndex(s => s.name === serviceName);
    if (currentIndex <= 0) return; // Already at the top

    setUpdatingServices(true);
    setError(null);

    try {
      const updatedServices = [...serviceList];
      
      // Swap with previous service
      [updatedServices[currentIndex - 1], updatedServices[currentIndex]] = 
      [updatedServices[currentIndex], updatedServices[currentIndex - 1]];
      
      // Update displayOrder for all services
      const reorderedServices = updatedServices.map((service, index) => ({
        ...service,
        displayOrder: index + 1
      }));

      const serviceListRef = doc(db, 'service_list', 'main');
      
      await updateDoc(serviceListRef, {
        services: reorderedServices,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.uid
      });
    } catch (err) {
      setError('Failed to reorder services. Please try again.');
      console.error('Error moving service up:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  // NEW: Move service down in the list
  const moveServiceDown = async (serviceName) => {
    if (!user?.uid || !isAdmin) {
      throw new Error('Admin access required to reorder services');
    }

    const currentIndex = serviceList.findIndex(s => s.name === serviceName);
    if (currentIndex < 0 || currentIndex >= serviceList.length - 1) return; // Already at the bottom

    setUpdatingServices(true);
    setError(null);

    try {
      const updatedServices = [...serviceList];
      
      // Swap with next service
      [updatedServices[currentIndex], updatedServices[currentIndex + 1]] = 
      [updatedServices[currentIndex + 1], updatedServices[currentIndex]];
      
      // Update displayOrder for all services
      const reorderedServices = updatedServices.map((service, index) => ({
        ...service,
        displayOrder: index + 1
      }));

      const serviceListRef = doc(db, 'service_list', 'main');
      
      await updateDoc(serviceListRef, {
        services: reorderedServices,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.uid
      });
    } catch (err) {
      setError('Failed to reorder services. Please try again.');
      console.error('Error moving service down:', err);
      throw err;
    } finally {
      setUpdatingServices(false);
    }
  };

  return {
    // State
    services: services || [],
    customerServices: services || [], // Alias for components that use this name
    serviceList: serviceList || [],
    serviceListMeta,
    loading,
    updatingServices,
    error,
    
    // Service management
    createService,
    updateService,
    deleteService,
    updateCustomerService: updateService, // Alias for compatibility
    
    // Service list management
    addServiceToList,
    updateServiceInList,
    deleteServiceFromList,
    moveServiceUp,      // NEW
    moveServiceDown,    // NEW
    
    // Getters
    getServiceStats,
    getServicesByStatus,
    getServicesByCustomer,
    getUpcomingServices,
    
    // Utilities
    clearError: () => setError(null)
  };
};

export default useAdminServices;